import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Clock, LogIn, LogOut, Calendar, AlertCircle, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const MIN_PUNCHES = 2;

const ClockInOutTab = () => {
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [punches, setPunches] = useState([]);
  const [activeClock, setActiveClock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [locationSettings, setLocationSettings] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  useEffect(() => {
    loadData();
    loadLocationSettings();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const loadLocationSettings = async () => {
    try {
      const res = await axios.get('/clock/location-settings');
      setLocationSettings(res.data);
      if (res.data.clock_location_required) {
        refreshLocation();
      }
    } catch (error) {
      console.error('Error loading location settings:', error);
    }
  };

  const refreshLocation = () => {
    setLocationError(null);
    if (!navigator.geolocation) {
      setLocationError('Geolocalización no soportada');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      (err) => setLocationError('No se pudo obtener ubicación: ' + err.message),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const loadData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const [historyRes, activeRes] = await Promise.all([
        axios.get(`/clock/history?date=${today}`),
        axios.get('/clock/active')
      ]);
      const sortedPunches = (historyRes.data || []).sort((a, b) => new Date(a.clock_in) - new Date(b.clock_in));
      setPunches(sortedPunches);
      setActiveClock(activeRes.data || null);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) { resolve({ latitude: 0, longitude: 0 }); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => resolve({ latitude: 0, longitude: 0 }),
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
      );
    });
  };

  const handleClockIn = async () => {
    setProcessing(true);
    try {
      const location = await getLocation();
      await axios.post(`/clock/in?latitude=${location.latitude}&longitude=${location.longitude}&notes=Ponche automático`);
      toast.success('Clock IN registrado');
      await new Promise(r => setTimeout(r, 500));
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar entrada');
    } finally {
      setProcessing(false);
    }
  };

  const handleClockOut = async () => {
    setProcessing(true);
    try {
      const location = await getLocation();
      await axios.post(`/clock/out?latitude=${location.latitude}&longitude=${location.longitude}&notes=Ponche automático`);
      toast.success('Clock OUT registrado');
      await new Promise(r => setTimeout(r, 500));
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar salida');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeletePunch = async (clockId) => {
    if (!window.confirm('¿Eliminar este ponche?')) return;
    try {
      await axios.delete(`/clock/${clockId}`);
      toast.success('Ponche eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar ponche');
    }
  };

  const countPunches = () => {
    let count = 0;
    punches.forEach(punch => { count++; if (punch.clock_out) count++; });
    return count;
  };

  const totalPunches = countPunches();
  const meetsMinimum = totalPunches >= MIN_PUNCHES;

  const formatTime = (date) => {
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatClockTime = (isoStr) => {
    if (!isoStr) return '--:--';
    return new Date(isoStr).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Clock className="w-12 h-12 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="clock-panel">
      {/* Header with time */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Reloj de Ponche</h2>
              <p className="text-slate-600 mt-1">Mínimo {MIN_PUNCHES} ponches por día</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-mono font-bold text-blue-600" data-testid="clock-time">
                {formatTime(currentTime)}
              </div>
              <div className="text-sm text-slate-600">{formatDate(currentTime)}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Location Status */}
      {locationSettings?.clock_location_required && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${userLocation ? 'bg-green-500' : locationError ? 'bg-red-500' : 'bg-amber-500 animate-pulse'}`}></div>
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {userLocation ? 'Ubicación GPS activa' : locationError ? 'Error de ubicación' : 'Obteniendo ubicación...'}
                  </p>
                  {userLocation && (
                    <p className="text-xs text-slate-500">
                      Lat: {userLocation.latitude.toFixed(6)}, Lon: {userLocation.longitude.toFixed(6)} | Radio permitido: {locationSettings.clock_radius_meters}m
                    </p>
                  )}
                  {locationError && <p className="text-xs text-red-500">{locationError}</p>}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={refreshLocation} data-testid="refresh-location-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4 flex-wrap">
            <Button onClick={handleClockIn} disabled={!!activeClock || processing} size="lg"
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-lg px-8 py-6" data-testid="clock-in-btn">
              <LogIn className="w-6 h-6 mr-2" /> Clock IN
            </Button>
            <Button onClick={handleClockOut} disabled={!activeClock || processing} size="lg"
              className="bg-red-600 hover:bg-red-700 text-lg px-8 py-6" data-testid="clock-out-btn">
              <LogOut className="w-6 h-6 mr-2" /> Clock OUT
            </Button>
            <div className="ml-auto flex gap-2">
              <Badge className={`text-sm py-2 px-4 ${activeClock ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-700'}`}>
                Estado: {activeClock ? 'En turno' : 'Sin ponche'}
              </Badge>
              <Badge className={`text-sm py-2 px-4 ${meetsMinimum ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid="punch-count">
                Ponches hoy: {totalPunches}/{MIN_PUNCHES}
              </Badge>
            </div>
          </div>
          {!meetsMinimum && (
            <div className="mt-4 flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Faltan {MIN_PUNCHES - totalPunches} ponche(s) para completar el mínimo diario</span>
            </div>
          )}
          {meetsMinimum && (
            <div className="mt-4 flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Cumple el mínimo de {MIN_PUNCHES} ponches hoy</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* History Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5" /> Historial de Hoy</CardTitle>
        </CardHeader>
        <CardContent>
          {punches.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Hora</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">Horas</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {punches.map((punch, idx) => (
                    <tr key={punch.clock_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <Badge className={punch.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                          {punch.status === 'active' ? 'IN' : 'OUT'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono">
                        {formatClockTime(punch.clock_in)}
                        {punch.clock_out && ` → ${formatClockTime(punch.clock_out)}`}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-right">
                        {punch.hours_worked ? `${punch.hours_worked.toFixed(2)}h` : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button variant="ghost" size="sm" onClick={() => handleDeletePunch(punch.clock_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={punch.status === 'active'}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No hay ponches registrados hoy</p>
              <p className="text-sm mt-2">Haz click en Clock IN para comenzar</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClockInOutTab;
